import {
  Check, Loader2, Pencil, Save, Trash2,
} from "lucide-react";
import { useState } from "react";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { Button } from "@/shared/ui/Button";
import { Checkbox } from "@/shared/ui/Checkbox";
import { Input } from "@/shared/ui/Input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";
import {
  CELL_EDITABLE_INPUT_CLASS,
  getAllocationCategoryEmoji,
  getAllocationCategoryLabel,
} from "./utils";
import type { EditableAllocationState } from "./utils";
import type { LedgerAllocation } from "../../types/accounting.types";

type StockAllocationsListProps = {
  allocations: LedgerAllocation[]
  getState: (allocation: LedgerAllocation) => EditableAllocationState
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  isAllocationDirty: (allocation: LedgerAllocation) => boolean
  onSave: (allocation: LedgerAllocation) => void
  onDelete: (id: string) => void
  isBusy: boolean
  onOpenStockEditor: (allocationId: string) => void
  deletingAllocationId: string | null
  // Selection mode
  isSelectionMode?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  highlightedAllocationId?: string | null
};

const StockAllocationsList = ({
  allocations,
  getState,
  setField,
  isAllocationDirty,
  onSave,
  onDelete,
  isBusy,
  onOpenStockEditor,
  deletingAllocationId,
  isSelectionMode = false,
  selectedIds,
  onToggleSelect,
  highlightedAllocationId,
}: StockAllocationsListProps) => {
  const allLocked = allocations.length > 0 && allocations.every((a) => a.locked);

  return (
    <div>
      <div className="mb-1.5 text-[11px] font-medium text-slate-600">Écritures stock</div>
      <div className="rounded-md border border-slate-200 bg-white overflow-x-hidden overflow-y-auto select-text">
        <table className="w-full table-fixed leading-tight">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {isSelectionMode && <th className="px-1 py-1 w-[28px]" />}
              <th className="text-left px-1 py-1 w-[28px] text-[9px] uppercase tracking-wide text-slate-600"> </th>
              <th className="text-left px-2 py-1 w-[90px] text-[9px] uppercase tracking-wide text-slate-600">Date</th>
              <th className="text-left px-2 py-1 w-[64px] text-[9px] uppercase tracking-wide text-slate-600">Budget</th>
              <th className="text-left px-2 py-1 text-[9px] uppercase tracking-wide text-slate-600" colSpan={2}>Référence</th>
              <th className="text-right px-2 py-1 w-[88px] text-[9px] uppercase tracking-wide text-slate-600">Débit</th>
              <th className="text-right px-2 py-1 w-[88px] text-[9px] uppercase tracking-wide text-slate-600">Crédit</th>
              {!allLocked && (
                <th className="text-right px-2 py-1 w-[96px] text-[9px] uppercase tracking-wide text-slate-600">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="text-[12px]">
            {allocations.map((allocation) => (
              <StockAllocationRow
                key={allocation.id}
                allocation={allocation}
                row={getState(allocation)}
                isDirty={isAllocationDirty(allocation)}
                onSave={() => onSave(allocation)}
                onDelete={() => onDelete(allocation.id)}
                isBusy={isBusy}
                setField={setField}
                onOpenStockEditor={() => onOpenStockEditor(allocation.id)}
                isDeleting={deletingAllocationId === allocation.id}
                isSelectionMode={isSelectionMode}
                isSelected={selectedIds?.has(allocation.id) ?? false}
                onToggleSelect={() => onToggleSelect?.(allocation.id)}
                isHighlighted={highlightedAllocationId === allocation.id}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

type StockAllocationRowProps = {
  allocation: LedgerAllocation
  row: EditableAllocationState
  isDirty: boolean
  onSave: () => void
  onDelete: () => void
  isBusy: boolean
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  onOpenStockEditor: () => void
  isDeleting: boolean
  isSelectionMode?: boolean
  isSelected?: boolean
  onToggleSelect?: () => void
  isHighlighted?: boolean
};

const StockAllocationRow = ({
  allocation,
  row,
  isDirty,
  onSave,
  onDelete,
  isBusy,
  setField,
  onOpenStockEditor,
  isDeleting,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelect,
  isHighlighted = false,
}: StockAllocationRowProps) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const emoji = getAllocationCategoryEmoji("stock");
  const categoryLabel = getAllocationCategoryLabel("stock");
  const stockRef = allocation.stockName || "-";
  const formatQuantity = (value: number | null) =>
    value !== null && value !== undefined ? value.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : "-";

  const LOCKED_CELL_CLASS = "h-7 px-2 flex items-center text-[12px] text-slate-500";

  if (allocation.locked) {
    return (
      <tr className="border-b border-slate-100 last:border-b-0 align-top bg-slate-50/50">
        {isSelectionMode && <td className="px-1 py-1.5 text-center align-middle" />}
        <td className="px-1 py-1.5 text-center align-middle">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-6 w-6 items-center justify-center text-[14px] leading-none cursor-default">{emoji}</span>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">{categoryLabel}</TooltipContent>
          </Tooltip>
        </td>
        <td className="py-1.5"><div className={LOCKED_CELL_CLASS}>{row.date || "-"}</div></td>
        <td className="py-1.5"><div className={LOCKED_CELL_CLASS}>STOCK</div></td>
        <td className="py-1.5 min-w-0" colSpan={2}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={`${LOCKED_CELL_CLASS} truncate`}>{stockRef}</div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[680px] break-words">{stockRef}</TooltipContent>
          </Tooltip>
        </td>
        <td className="py-1.5">
          <div className={`${LOCKED_CELL_CLASS} justify-end tabular-nums`}>{formatQuantity(allocation.stockDebitQuantity)}</div>
        </td>
        <td className="py-1.5">
          <div className={`${LOCKED_CELL_CLASS} justify-end tabular-nums`}>{formatQuantity(allocation.stockCreditQuantity)}</div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b border-slate-100 last:border-b-0 align-top${isDeleting ? " opacity-50" : ""}${isHighlighted ? " bg-amber-50 ring-1 ring-inset ring-amber-300" : isSelected ? " bg-indigo-50/50" : ""}`}>
      {isSelectionMode && (
        <td className="px-1 py-1.5 text-center align-middle">
          {allocation.settlementId
            ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Checkbox checked={false} disabled className="size-3.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs max-w-[200px]">
                  Liée à un règlement — déplacez le règlement pour déplacer ses écritures
                </TooltipContent>
              </Tooltip>
            )
            : (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelect?.()}
                className="size-3.5"
              />
            )}
        </td>
      )}
      <td className="px-1 py-1.5 text-center align-middle">
        {isDeleting
          ? <Loader2 className="inline h-4 w-4 animate-spin text-slate-400" />
          : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex h-6 w-6 items-center justify-center text-[14px] leading-none cursor-default">{emoji}</span>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">{categoryLabel}</TooltipContent>
            </Tooltip>
          )}
      </td>
      <td className="p-0">
        <Input
          type="date"
          min={ACCOUNTING_DATE_BOUNDS.min}
          max={ACCOUNTING_DATE_BOUNDS.max}
          value={row.date}
          onChange={(event) => setField(allocation.id, "date", event.target.value, allocation)}
          className={CELL_EDITABLE_INPUT_CLASS}
        />
      </td>
      <td className="p-0">
        <div className="h-7 px-2 flex items-center text-[12px] text-slate-500 font-medium">STOCK</div>
      </td>
      <td className="p-0 min-w-0" colSpan={2}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="h-7 px-2 flex items-center text-[12px] text-slate-700 truncate">{stockRef}</div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[680px] break-words">{stockRef}</TooltipContent>
        </Tooltip>
      </td>
      <td className="p-0">
        <div className="h-7 px-2 flex items-center justify-end text-[12px] tabular-nums text-slate-700">
          {formatQuantity(allocation.stockDebitQuantity)}
        </div>
      </td>
      <td className="p-0">
        <div className="h-7 px-2 flex items-center justify-end text-[12px] tabular-nums text-slate-700">
          {formatQuantity(allocation.stockCreditQuantity)}
        </div>
      </td>
      <td className="px-2 py-1.5">
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onOpenStockEditor}
            disabled={isBusy}
            aria-label="Éditer l'écriture stock"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant={isDirty ? "success" : "outline"}
            size="sm"
            className="h-6 w-6 p-0"
            onClick={onSave}
            disabled={isBusy || !isDirty}
            aria-label="Enregistrer l'écriture"
          >
            <Save className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant={isConfirmingDelete ? "destructive" : "destructiveGhost"}
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => {
              if (isConfirmingDelete) {
                onDelete();
                setIsConfirmingDelete(false);
              }
              else {
                setIsConfirmingDelete(true);
              }
            }}
            onBlur={() => setIsConfirmingDelete(false)}
            disabled={isBusy}
            aria-label={isConfirmingDelete ? "Confirmer la suppression" : "Supprimer l'écriture"}
          >
            {isConfirmingDelete ? <Check className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default StockAllocationsList;
